import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSongs } from '../../config/SongContext';
import styles from './SharedSongLayout.module.scss';
import Close from '../Icons/Close';
import Record from '../Icons/Record';
import Footer from '../Footer/Footer';
import ScrollContainer from '../ScrollContainer/ScrollContainer';

const SCROLL_THRESHOLD = 100;

/**
 * Bare-chrome wrapper for shared song links (`/song/:id?shared=1`).
 * Renders a focused header with an "Open in Good Song Club" CTA that is
 * auth-aware: authed users go straight to the in-app song view, guests are
 * sent to /login with a returnTo so they land back on the song after signing
 * in.
 *
 * The body slot is intentionally dumb — callers pass the song content in
 * (in practice: the matched `/song/:id` route element, which renders a
 * `SongPage standalone`).
 */
const SharedSongLayout = ({ children }) => {
  const { currentSong } = useSongs();
  const bodyContainerRef = useRef(null);
  const [navbarState, setNavbarState] = useState('initial');

  /*
    Mirrors SongPage's scroll-reveal NavBar: past SCROLL_THRESHOLD we swap
    the brand lockup for the song title/artist using a translate+blur
    cross-fade. The outer .bodyContainer is the scroll surface in this
    layout (SongPage's inner ScrollContainer is disabled in standalone
    mode). `bodyContainerRef` is a ScrollContainer proxy, so `scrollTop`
    and `addEventListener('scroll', ...)` transparently forward to the
    underlying OverlayScrollbars viewport.
  */
  useEffect(() => {
    const el = bodyContainerRef.current;
    if (!el) return undefined;
    const handleScroll = () => {
      setNavbarState(el.scrollTop > SCROLL_THRESHOLD ? 'scrolled' : 'initial');
    };
    handleScroll();
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [currentSong?.id]);

  /*
    Interactive descendants (comment cards, buttons, the Player's control
    row, etc.) occasionally swallow wheel events before they reach the
    OverlayScrollbars host. Forwarding the delta to `bodyContainerRef`'s
    viewport — same pattern used by SettingsPage / NotificationsPage /
    SearchPage — guarantees wheel scrolls the page no matter where the
    cursor sits. `data-has-own-scroll` (below) tells the App shell to
    leave its own forwarder out of the chain, so there is no double apply.
  */
  const handleWheelCapture = useCallback((e) => {
    const el = bodyContainerRef.current;
    if (!el) return;
    const isVertical = Math.abs(e.deltaY) > Math.abs(e.deltaX);
    if (!isVertical) return;
    el.scrollTop += e.deltaY;
    e.stopPropagation();
  }, []);

  const songName = currentSong?.songName;
  const songArtist = currentSong?.songArtist;
  const isScrolled = navbarState === 'scrolled';

  return (
    <div className={styles.container}>
      <div className={styles.layout}>
        <header className={styles.header}>
          <div className={styles.titleArea}>
            <div
              className={`${styles.titleContent} ${styles[navbarState]}`}
            >
              <Link
                to="/"
                className={styles.brandMark}
                aria-label="Good Song Club home"
              >
                <Record />
              </Link>
              <div className={styles.textSwap}>
                <span
                  className={styles.brandText}
                  aria-hidden={isScrolled ? 'true' : undefined}
                >
                  Good Song Club
                </span>
                <div
                  className={styles.songInfo}
                  aria-hidden={isScrolled ? undefined : 'true'}
                >
                  <div className={styles.songName}>{songName}</div>
                  <div className={styles.songArtist}>{songArtist}</div>
                </div>
              </div>
            </div>
          </div>
          <Link to="/" className={styles.openInAppButton}>
            <Close />
          </Link>
        </header>
        <ScrollContainer
          ref={bodyContainerRef}
          className={styles.bodyContainer}
          data-has-own-scroll
          onWheelCapture={handleWheelCapture}
        >
          <div className={styles.bodyInner}>
            <main className={styles.body}>{children}</main>
            <Footer />
          </div>
        </ScrollContainer>
      </div>
    </div>
  );
};

export default SharedSongLayout;
